import React, { Fragment, useEffect } from "react";
import logo from "../../../assets/media/logo.png";
import GamingBoardBahamasLogo from "../../../assets/media/GamingBoardBahamasLogo.png";
import iconMark from "../../../assets/media/icon-mark.png";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import GBInput from "../../input";
import * as Yup from "yup";
import {
  clearMfaChallenge,
  resendLoginOtp,
  verifyLoginOtp,
} from "../../../store/reducers/authSlice";
import { getHomePath } from "../../../utility/rbac";
import { showError } from "../../../helpers/toast";

/**
 * Login SMS MFA step (testing results row 327 — SMS on every login).
 * Shown after /auth/login returns an MFA challenge instead of a full session.
 */
const LoginMfa = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const mfa = useSelector((state) => state.auth.mfa);
  const loading = useSelector((state) => state.auth.loading);

  const { values, handleChange, handleSubmit, errors, touched } = useFormik({
    initialValues: { otp: "" },
    validationSchema: Yup.object({
      otp: Yup.string()
        .required("OTP is required")
        .matches(/^\d{4,8}$/, "Enter the OTP sent to your phone"),
    }),
    onSubmit: async (formValues) => {
      if (!mfa?.token) {
        showError("Login session expired. Please sign in again.");
        navigate("/");
        return;
      }
      try {
        await dispatch(
          verifyLoginOtp({
            otp: String(formValues.otp).trim(),
            mfaToken: mfa.token,
          })
        ).unwrap();
      } catch (err) {
        if (err?.expired) {
          showError(err.message || "Verification session expired. Please sign in again.");
          dispatch(clearMfaChallenge());
          navigate("/");
        }
      }
    },
  });

  useEffect(() => {
    if (!mfa?.token) {
      navigate("/");
    }
  }, [mfa?.token, navigate]);

  useEffect(() => {
    if (user?.user && user?.access_token) {
      navigate(getHomePath(user));
    }
  }, [navigate, user]);

  const handleResend = async () => {
    if (!mfa?.token) return;
    try {
      await dispatch(resendLoginOtp({ mfaToken: mfa.token })).unwrap();
    } catch (err) {
      if (err?.expired) {
        showError(err.message || "Verification session expired. Please sign in again.");
        dispatch(clearMfaChallenge());
        navigate("/");
      }
    }
  };

  const handleBack = () => {
    dispatch(clearMfaChallenge());
    navigate("/");
  };

  const phoneHint = mfa?.phoneHint ? ` (${mfa.phoneHint})` : "";

  return (
    <div className="App">
      <div className="container">
        <div className="landingWrapper">
          <div className="landingRow">
            <div className="landingCol">
              <div className="fieldsWrapper">
                <img src={logo} className="logo" alt="logo" />
                <h2>Verify your identity</h2>
                <p>
                  We have sent an OTP to your registered phone number
                  {phoneHint}. Enter it to continue signing in.
                </p>

                <form className="gb-login-form" onSubmit={handleSubmit}>
                  <Fragment>
                    <GBInput
                      name="otp"
                      type="text"
                      label="OTP"
                      value={values.otp}
                      placeholder="Enter OTP"
                      setValue={handleChange}
                      iconSrc={iconMark}
                    />
                    {touched.otp && errors.otp && (
                      <div className="error-message error-message-wrap">
                        {errors.otp}
                      </div>
                    )}
                  </Fragment>

                  <div className="gb-field-wrap">
                    <button
                      type="button"
                      className="forget-link border-0 bg-transparent p-0"
                      onClick={handleResend}
                      disabled={loading}
                    >
                      Resend OTP
                    </button>
                  </div>

                  <div className="gb-form-footer">
                    <button
                      type="submit"
                      className="form-submit"
                      disabled={loading || !values.otp}
                      style={{
                        background:
                          loading || !values.otp ? "#ccc" : undefined,
                        border:
                          loading || !values.otp
                            ? "solid 1px #ccc"
                            : undefined,
                      }}
                    >
                      {loading ? (
                        <>
                          <div
                            className="text-xs spinner-border text-light"
                            style={{ width: "1rem", height: "1rem" }}
                            role="status"
                          />
                          <span className="sr-only ml-5"> Loading...</span>
                        </>
                      ) : (
                        "Verify & Sign In"
                      )}
                    </button>
                  </div>
                </form>

                <div className="loginBox mt-3">
                  <p>
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0"
                      onClick={handleBack}
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      Back to Sign In
                    </button>
                  </p>
                </div>
              </div>
            </div>
            <div className="landingCol">
              <div className="imageWrapper">
                <div className="loginBox">
                  <p>
                    Already have account? <Link to={"/"}>Sign In</Link>
                  </p>
                </div>
                <img
                  width={"300px"}
                  height={"300px"}
                  src={GamingBoardBahamasLogo}
                  className="booksAnimation"
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginMfa;
